import { logger } from '@/ui'

/**
 * Hook for managing pending onboarding values in localStorage
 * Used for the marketing page -> auth -> campaign creation flow
 * Supports both position (manual mode) and URL (scan mode)
 */
const POSITION_STORAGE_KEY = 'jobilla_pending_position'
const URL_STORAGE_KEY = 'jobilla_pending_url'
const REDIRECT_STORAGE_KEY = 'jobilla_pending_redirect'
const INVITE_TOKEN_KEY = 'jobilla_pending_invite_token'

export function usePositionStorage() {
	const savePosition = (position: string) => {
		try {
			localStorage.setItem(POSITION_STORAGE_KEY, position)
		} catch (error) {
			console.error('Failed to save position to localStorage:', error)
		}
	}

	const getPosition = (): string | null => {
		try {
			return localStorage.getItem(POSITION_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to get position from localStorage:', error)
			return null
		}
	}

	const clearPosition = () => {
		try {
			localStorage.removeItem(POSITION_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to clear position from localStorage:', error)
		}
	}

	const saveUrl = (url: string) => {
		try {
			localStorage.setItem(URL_STORAGE_KEY, url)
		} catch (error) {
			console.error('Failed to save URL to localStorage:', error)
		}
	}

	const getUrl = (): string | null => {
		try {
			return localStorage.getItem(URL_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to get URL from localStorage:', error)
			return null
		}
	}

	const clearUrl = () => {
		try {
			localStorage.removeItem(URL_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to clear URL from localStorage:', error)
		}
	}

	const getCompanyUrl = (slug: string): string => {
		const pendingUrl = getUrl()
		const pendingPosition = getPosition()

		if (pendingUrl) {
			return `/company/${slug}/campaign/new?url=${encodeURIComponent(pendingUrl)}`
		}

		if (pendingPosition) {
			return `/company/${slug}/campaign/new?position=${encodeURIComponent(pendingPosition)}`
		}

		return `/company/${slug}`
	}

	const saveRedirect = (url: string) => {
		try {
			localStorage.setItem(REDIRECT_STORAGE_KEY, url)
		} catch (error) {
			console.error('Failed to save redirect to localStorage:', error)
		}
	}

	const getRedirect = (): string | null => {
		try {
			return localStorage.getItem(REDIRECT_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to get redirect from localStorage:', error)
			return null
		}
	}

	const clearRedirect = () => {
		try {
			localStorage.removeItem(REDIRECT_STORAGE_KEY)
		} catch (error) {
			console.error('Failed to clear redirect from localStorage:', error)
		}
	}

	const saveInviteToken = (token: string) => {
		try {
			sessionStorage.setItem(INVITE_TOKEN_KEY, token)
		} catch (error) {
			logger.error(error, { category: 'storage', data: { action: 'save-invite-token' } })
		}
	}

	const getInviteToken = (): string | null => {
		try {
			return sessionStorage.getItem(INVITE_TOKEN_KEY)
		} catch (error) {
			logger.error(error, { category: 'storage', data: { action: 'get-invite-token' } })
			return null
		}
	}

	const clearInviteToken = () => {
		try {
			sessionStorage.removeItem(INVITE_TOKEN_KEY)
		} catch (error) {
			logger.error(error, { category: 'storage', data: { action: 'clear-invite-token' } })
		}
	}

	return {
		savePosition,
		getPosition,
		clearPosition,
		saveUrl,
		getUrl,
		clearUrl,
		getCompanyUrl,
		saveRedirect,
		getRedirect,
		clearRedirect,
		saveInviteToken,
		getInviteToken,
		clearInviteToken,
	}
}
